import {
  GROWTH_STORAGE_KEY,
  GROWTH_BACKUP_KEY,
  GROWTH_LOCAL_BACKUP_KEY,
} from './schema';

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const createLearningResource = (overrides = {}) => ({
  id: uid(),
  type: 'course',
  title: '',
  institution: '',
  url: '',
  estimatedHours: 0,
  status: 'pending',
  progress: 0,
  startDate: '',
  notes: '',
  skills: [],
  ...overrides,
});

export const createPlacement = (resourceId, overrides = {}) => ({
  id: uid(),
  resourceId,
  targetDate: '',
  ...overrides,
});

export function getResourceMap(learningResources = []) {
  return new Map((learningResources || []).map((r) => [r.id, r]));
}

export function resolvePlacement(placement, resourceMap) {
  if (!placement) return null;
  const map = resourceMap instanceof Map ? resourceMap : getResourceMap(resourceMap);

  if (placement.resourceId) {
    const resource = map.get(placement.resourceId);
    if (!resource) return null;
    return {
      ...resource,
      placementId: placement.id,
      resourceId: placement.resourceId,
      targetDate: placement.targetDate ?? resource.targetDate ?? '',
    };
  }

  return {
    ...placement,
    placementId: placement.id,
    resourceId: placement.id,
  };
}

export function resolveStageItems(stage, learningResources = []) {
  const map = getResourceMap(learningResources);
  return (stage?.items || [])
    .map((p) => resolvePlacement(p, map))
    .filter(Boolean);
}

export function computeStageEstimatedHours(stage, learningResources = []) {
  return resolveStageItems(stage, learningResources).reduce(
    (sum, item) => sum + (Number(item.estimatedHours) || 0),
    0
  );
}

export function computeRouteEstimatedHours(route, learningResources = []) {
  return (route?.stages || []).reduce(
    (sum, stage) => sum + computeStageEstimatedHours(stage, learningResources),
    0
  );
}

export function searchResources(learningResources = [], query = '') {
  const q = query.trim().toLowerCase();
  if (!q) return learningResources;
  return learningResources.filter((r) =>
    [r.title, r.institution, r.url, r.type]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q))
  );
}

export function inlineItemToResource(item) {
  return {
    id: item.id,
    type: item.type || 'course',
    title: item.title || '',
    institution: item.institution || '',
    url: item.url || '',
    estimatedHours: Number(item.estimatedHours) || 0,
    status: item.status || 'pending',
    progress: Number(item.progress) || 0,
    startDate: item.startDate || '',
    notes: item.notes || '',
    skills: item.skills || [],
  };
}

export function isPlacement(item) {
  return Boolean(item?.resourceId);
}

export function isInlineItem(item) {
  if (!item || item.resourceId) return false;
  return Boolean(item.title || item.url || item.type);
}

export function normalizeText(value = '') {
  return String(value).trim().toLowerCase().replace(/\s+/g, ' ');
}

export function normalizeUrl(url = '') {
  return String(url).trim().toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '');
}

export function resourceFingerprint(resource = {}) {
  const title = normalizeText(resource.title || '');
  const url = normalizeUrl(resource.url || '');
  if (!title && !url) return null;
  return url ? `${title}::${url}` : title;
}

const STATUS_RANK = { completed: 4, in_progress: 3, pending: 2, skipped: 1 };

export function mergeResourceData(primary = {}, secondary = {}) {
  const pickStatus = (a, b) => {
    const rankA = STATUS_RANK[a] || 0;
    const rankB = STATUS_RANK[b] || 0;
    if (rankB > rankA) return b;
    if (rankA > rankB) return a;
    return a || b;
  };

  return {
    ...primary,
    title: primary.title || secondary.title || '',
    url: primary.url || secondary.url || '',
    institution: primary.institution || secondary.institution || '',
    type: primary.type || secondary.type || 'course',
    estimatedHours: Math.max(Number(primary.estimatedHours) || 0, Number(secondary.estimatedHours) || 0),
    progress: Math.max(Number(primary.progress) || 0, Number(secondary.progress) || 0),
    status: pickStatus(primary.status, secondary.status),
    startDate: primary.startDate || secondary.startDate || '',
    notes: [primary.notes, secondary.notes].filter(Boolean).join('\n').trim() || '',
    skills: [...new Set([...(primary.skills || []), ...(secondary.skills || [])])],
  };
}

function pickCanonicalId(ids, resourceMap) {
  const sorted = [...ids].sort((a, b) => {
    const ra = resourceMap.get(a);
    const rb = resourceMap.get(b);
    const rankA = STATUS_RANK[ra?.status] || 0;
    const rankB = STATUS_RANK[rb?.status] || 0;
    if (rankB !== rankA) return rankB - rankA;
    return (Number(rb?.progress) || 0) - (Number(ra?.progress) || 0);
  });
  return sorted[0] || ids[0];
}

function dedupeStagePlacements(items = []) {
  const seen = new Set();
  return items.filter((placement) => {
    const key = placement.resourceId || placement.id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function dedupeResourcesAndRoutes(learningResources = [], routes = []) {
  const resourceMap = new Map((learningResources || []).map((r) => [r.id, r]));
  const fingerprintGroups = new Map();
  const idRemap = new Map();
  const mergedGroups = [];

  resourceMap.forEach((resource, id) => {
    const fp = resourceFingerprint(resource);
    if (!fp) return;
    if (!fingerprintGroups.has(fp)) fingerprintGroups.set(fp, []);
    fingerprintGroups.get(fp).push(id);
  });

  fingerprintGroups.forEach((ids, fp) => {
    if (ids.length < 2) return;

    const canonicalId = pickCanonicalId(ids, resourceMap);
    let canonical = { ...resourceMap.get(canonicalId) };

    ids.forEach((id) => {
      if (id === canonicalId) return;
      canonical = mergeResourceData(canonical, resourceMap.get(id));
      idRemap.set(id, canonicalId);
      resourceMap.delete(id);
    });

    resourceMap.set(canonicalId, canonical);
    mergedGroups.push({
      fingerprint: fp,
      title: canonical.title,
      url: canonical.url || '',
      canonicalId,
      mergedIds: ids.filter((id) => id !== canonicalId),
      mergedCount: ids.length,
    });
  });

  let placementRemaps = 0;
  const nextRoutes = (routes || []).map((route) => ({
    ...route,
    stages: (route.stages || []).map((stage) => {
      const items = dedupeStagePlacements((stage.items || []).map((placement) => {
        const mappedId = idRemap.get(placement.resourceId);
        if (!mappedId) return placement;
        placementRemaps += 1;
        return { ...placement, resourceId: mappedId };
      }));
      return { ...stage, items };
    }),
  }));

  return {
    learningResources: [...resourceMap.values()],
    routes: nextRoutes,
    mergedGroups,
    placementRemaps,
  };
}

function registerResource(resource, resourceMap, fingerprintIndex) {
  const fp = resourceFingerprint(resource);
  if (fp && fingerprintIndex.has(fp)) {
    const canonicalId = fingerprintIndex.get(fp);
    const existing = resourceMap.get(canonicalId);
    resourceMap.set(canonicalId, mergeResourceData(existing, resource));
    return { resourceId: canonicalId, merged: true, fingerprint: fp };
  }

  resourceMap.set(resource.id, resource);
  if (fp) fingerprintIndex.set(fp, resource.id);
  return { resourceId: resource.id, merged: false, fingerprint: fp };
}

export function migrateGrowthData(data) {
  const base = { ...data };
  if (!base.learningResources) base.learningResources = [];

  const resourceMap = new Map((base.learningResources || []).map((r) => [r.id, r]));
  const fingerprintIndex = new Map();
  let inlineConverted = 0;
  const inlineMerged = [];

  (base.learningResources || []).forEach((resource) => {
    const fp = resourceFingerprint(resource);
    if (fp && !fingerprintIndex.has(fp)) {
      fingerprintIndex.set(fp, resource.id);
    }
  });

  const routes = (base.routes || []).map((route) => ({
    ...route,
    stages: (route.stages || []).map((stage) => ({
      ...stage,
      items: (stage.items || []).map((item) => {
        if (isPlacement(item)) return item;
        if (!isInlineItem(item)) return item;

        inlineConverted += 1;
        const resource = inlineItemToResource(item);
        const { resourceId, merged } = registerResource(resource, resourceMap, fingerprintIndex);

        if (merged) {
          inlineMerged.push({ title: resource.title, url: resource.url || '' });
        }

        return createPlacement(resourceId, {
          id: item.id.startsWith('pl_') ? item.id : `pl_${item.id}`,
          targetDate: item.targetDate || '',
        });
      }),
    })),
  }));

  const deduped = dedupeResourcesAndRoutes([...resourceMap.values()], routes);
  const mergedGroups = deduped.mergedGroups;
  const changed = inlineConverted > 0
    || mergedGroups.length > 0
    || deduped.placementRemaps > 0
    || inlineMerged.length > 0;

  const migrationReport = changed
    ? {
      inlineConverted,
      inlineMergedByFingerprint: inlineMerged,
      mergedGroups,
      placementRemaps: deduped.placementRemaps,
      recoveredAt: new Date().toISOString(),
      dismissed: false,
    }
    : {
      inlineConverted: 0,
      inlineMergedByFingerprint: [],
      mergedGroups: [],
      placementRemaps: 0,
      ...(base.migrationReport || {}),
    };

  return {
    ...base,
    routes: deduped.routes,
    learningResources: deduped.learningResources,
    migrationReport,
    _migratedResources: true,
    _migrationVersion: 2,
    changed,
  };
}

export function removeResourceFromRoutes(routes, resourceId) {
  return routes.map((route) => ({
    ...route,
    stages: (route.stages || []).map((stage) => ({
      ...stage,
      items: (stage.items || []).filter((p) => p.resourceId !== resourceId),
    })),
  }));
}

export function stageHasResource(stage, resourceId) {
  return (stage?.items || []).some((p) => p.resourceId === resourceId);
}

export function getRouteResourceIds(route) {
  const ids = new Set();
  (route?.stages || []).forEach((stage) => {
    (stage.items || []).forEach((p) => {
      if (p.resourceId) ids.add(p.resourceId);
    });
  });
  return ids;
}

export function getCompletedItems(routes = [], learningResources = []) {
  const map = getResourceMap(learningResources);
  const seen = new Set();
  const items = [];
  routes.forEach((route) => {
    (route.stages || []).forEach((stage) => {
      (stage.items || []).forEach((placement) => {
        const resolved = resolvePlacement(placement, map);
        if (resolved?.status === 'completed' && !seen.has(resolved.resourceId)) {
          seen.add(resolved.resourceId);
          items.push(resolved);
        }
      });
    });
  });
  return items;
}

export function countRouteItems(routes = []) {
  return (routes || []).reduce(
    (sum, route) => sum + (route.stages || []).reduce(
      (stageSum, stage) => stageSum + (stage.items || []).length,
      0
    ),
    0
  );
}

function placementItemKey(item) {
  if (!item) return '';
  if (item.resourceId) return `placement:${item.resourceId}:${item.id || ''}`;
  const fp = resourceFingerprint(inlineItemToResource(item));
  return fp ? `inline:${fp}` : `inline:${item.id || ''}`;
}

function mergeStageItems(localItems = [], remoteItems = []) {
  if (!localItems.length && remoteItems.length) return [...remoteItems];
  if (!remoteItems.length && localItems.length) return [...localItems];

  const merged = [...localItems];
  const seen = new Set(localItems.map(placementItemKey));
  remoteItems.forEach((item) => {
    const key = placementItemKey(item);
    if (!seen.has(key)) {
      merged.push(item);
      seen.add(key);
    }
  });
  return merged;
}

function mergeStages(localStages = [], remoteStages = []) {
  const merged = (localStages || []).map((localStage) => {
    const remoteStage = (remoteStages || []).find((s) => s.id === localStage.id)
      || (remoteStages || []).find((s) => normalizeText(s.title) === normalizeText(localStage.title));
    if (!remoteStage) return localStage;
    return {
      ...localStage,
      ...remoteStage,
      id: localStage.id,
      title: localStage.title || remoteStage.title,
      items: mergeStageItems(localStage.items, remoteStage.items),
    };
  });

  (remoteStages || []).forEach((remoteStage) => {
    const exists = merged.some(
      (s) => s.id === remoteStage.id || normalizeText(s.title) === normalizeText(remoteStage.title)
    );
    if (!exists) merged.push(remoteStage);
  });

  return merged;
}

function mergeRoutes(localRoutes = [], remoteRoutes = []) {
  const merged = (localRoutes || []).map((localRoute) => {
    const remoteRoute = (remoteRoutes || []).find((r) => r.id === localRoute.id)
      || (remoteRoutes || []).find((r) => normalizeText(r.name) === normalizeText(localRoute.name));
    if (!remoteRoute) return localRoute;
    return {
      ...localRoute,
      ...remoteRoute,
      id: localRoute.id,
      name: localRoute.name || remoteRoute.name,
      stages: mergeStages(localRoute.stages, remoteRoute.stages),
    };
  });

  (remoteRoutes || []).forEach((remoteRoute) => {
    const exists = merged.some(
      (r) => r.id === remoteRoute.id || normalizeText(r.name) === normalizeText(remoteRoute.name)
    );
    if (!exists) merged.push(remoteRoute);
  });

  return merged;
}

function uniqueResources(resources = []) {
  const byId = new Map();
  resources.forEach((resource) => {
    if (!resource?.id) return;
    if (!byId.has(resource.id)) {
      byId.set(resource.id, resource);
      return;
    }
    byId.set(resource.id, mergeResourceData(byId.get(resource.id), resource));
  });
  return [...byId.values()];
}

export function recoverOrphanResources(routes = [], learningResources = []) {
  const referenced = new Set();
  (routes || []).forEach((route) => {
    (route.stages || []).forEach((stage) => {
      (stage.items || []).forEach((placement) => {
        if (placement.resourceId) referenced.add(placement.resourceId);
      });
    });
  });

  const orphans = (learningResources || []).filter((r) => r.id && !referenced.has(r.id));
  if (!orphans.length) return { routes, recovered: 0 };

  let recovered = 0;
  const nextRoutes = routes.map((route) => ({ ...route, stages: (route.stages || []).map((s) => ({ ...s, items: [...(s.items || [])] })) }));

  orphans.forEach((resource) => {
    const targetRoute = nextRoutes[0];
    if (!targetRoute) return;
    if (!targetRoute.stages?.length) {
      targetRoute.stages = [{ id: `recovered_${resource.id}`, title: 'Recuperados', items: [] }];
    }
    const targetStage = targetRoute.stages[0];
    if (stageHasResource(targetStage, resource.id)) return;
    targetStage.items.push(createPlacement(resource.id, { id: `pl_recovered_${resource.id}` }));
    recovered += 1;
  });

  return { routes: nextRoutes, recovered };
}

/**
 * Combina dos snapshots (local + remoto/backup). Prioriza la fuente con más items
 * por ruta/etapa y conserva recursos huérfanos del catálogo.
 */
export function mergeGrowthPlannerData(primary = {}, secondary = {}) {
  const primaryItems = countRouteItems(primary.routes);
  const secondaryItems = countRouteItems(secondary.routes);

  const routes = primaryItems >= secondaryItems
    ? mergeRoutes(primary.routes, secondary.routes)
    : mergeRoutes(secondary.routes, primary.routes);

  const learningResources = uniqueResources([
    ...(primary.learningResources || []),
    ...(secondary.learningResources || []),
  ]);

  const orphanRecovery = recoverOrphanResources(routes, learningResources);

  return migrateGrowthData({
    ...primary,
    ...secondary,
    routes: orphanRecovery.routes,
    learningResources,
    skills: [...(primary.skills || []), ...(secondary.skills || [])].filter(
      (skill, idx, arr) => arr.findIndex((s) => s.id === skill.id) === idx
    ),
    projects: [...(primary.projects || []), ...(secondary.projects || [])].filter(
      (project, idx, arr) => arr.findIndex((p) => p.id === project.id) === idx
    ),
    focus: primary.focus?.studyLog?.length >= (secondary.focus?.studyLog?.length || 0)
      ? primary.focus
      : secondary.focus || primary.focus,
  });
}

export function collectGrowthBackups() {
  if (typeof localStorage === 'undefined') return [];
  const snapshots = [];
  const seen = new Set();

  const tryAdd = (key, raw) => {
    if (!raw || seen.has(key)) return;
    try {
      const data = JSON.parse(raw);
      if (data?.routes || data?.learningResources?.length) {
        snapshots.push({ key, data });
        seen.add(key);
      }
    } catch {
      // ignore invalid backup
    }
  };

  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || !key.toLowerCase().includes('growthplanner')) continue;
    tryAdd(key, localStorage.getItem(key));
  }

  [GROWTH_BACKUP_KEY, GROWTH_LOCAL_BACKUP_KEY, `${GROWTH_STORAGE_KEY}_v1`].forEach((key) => {
    tryAdd(key, localStorage.getItem(key));
  });

  return snapshots;
}

export function recoverGrowthDataFromBackups(current = {}) {
  let best = { ...current };
  let bestCount = countRouteItems(current.routes);

  collectGrowthBackups().forEach(({ key, data }) => {
    const merged = mergeGrowthPlannerData(best, data);
    const count = countRouteItems(merged.routes);
    if (count > bestCount) {
      best = merged;
      bestCount = count;
    }
  });

  return best;
}
